// routes.ts
import { lazy } from 'react';


// Types for our data structures
export type SubCategory = {
    id: string;
    name: string;
    path: string;
};

export type Category = {
    id: string;
    name: string;
    icon: string;
    subCategories: SubCategory[];
    isOpen?: boolean;
};

// Define all categories and subcategories
export const categories: Category[] = [
     {
        id: 'data-formatters',
        name: 'Data Formatters',
        icon: '📊',
        isOpen: true,
        subCategories: [
            { id: 'json-formatter', name: 'JSON Formatter / Beautifier', path: '/json-formatter' },
            { id: 'xml-formatter', name: 'XML Formatter', path: '/xml-formatter' },
            { id: 'yaml-formatter', name: 'YAML Formatter', path: '/yaml-formatter' },
            { id: 'sql-formatter', name: 'SQL Formatter', path: '/sql-formatter' },
            { id: 'ini-formatter', name: 'INI Formatter', path: '/ini-formatter' },
        ]
    },
    {
        id: 'code-formatters',
        name: 'Code Formatters',
        icon: '💻',
        isOpen: false,
        subCategories: [
            { id: 'html-formatter', name: 'HTML Formatter', path: '/html-formatter' },
            { id: 'css-formatter', name: 'CSS Formatter', path: '/css-formatter' },
            { id: 'javascript-formatter', name: 'JavaScript Formatter', path: '/javascript-formatter' },
            { id: 'typescript-formatter', name: 'TypeScript Formatter', path: '/typescript-formatter' },
            { id: 'python-formatter', name: 'Python Formatter', path: '/python-formatter' },
            { id: 'php-formatter', name: 'PHP Formatter', path: '/php-formatter' },
            { id: 'java-formatter', name: 'Java Formatter', path: '/java-formatter' },
            { id: 'cpp-formatter', name: 'C++/C Formatter', path: '/cpp-formatter' },
        ]
    },
    {
        id: 'text-document-formatters',
        name: 'Text & Document Formatters',
        icon: '📄',
        isOpen: false,
        subCategories: [
            { id: 'markdown-formatter', name: 'Markdown Formatter', path: '/markdown-formatter' },
            { id: 'latex-formatter', name: 'LaTeX Formatter', path: '/latex-formatter' },
            { id: 'text-wrap-formatter', name: 'Text Wrapping & Indentation Formatter', path: '/text-wrap-formatter' },
        ]
    },
    {
        id: 'specialized-formatters',
        name: 'Specialized Formatters',
        icon: '🛠️',
        isOpen: false,
        subCategories: [
            { id: 'log-formatter', name: 'Log Formatter', path: '/log-formatter' },
            { id: 'jwt-formatter', name: 'JWT Decoder / Formatter', path: '/jwt-formatter' },
            { id: 'url-formatter', name: 'URL Encoder/Decoder & Formatter', path: '/url-formatter' },
            { id: 'base64-formatter', name: 'Base64 Formatter', path: '/base64-formatter' },
            { id: 'uuid-formatter', name: 'UUID Formatter', path: '/uuid-formatter' },
        ]
    }
];

// Helper function to find category and subcategory by IDs
export const findCategoryAndSubcategory = (categoryId?: string, subcategoryId?: string) => {
    const category = categoryId ? categories?.find(c => c.id === categoryId) : null;
    let subcategory = null;

    if (category && subcategoryId) {
        subcategory = category.subCategories.find(s => s.id === subcategoryId);
    }

    return { category, subcategory };
};

// For use in React Router config
export const getFormattorRoutes = () => {
    const routes = [];

    // Add the base converter route
    routes.push({
        path: '/formattor',
        element: lazy(() => import('../pages/frontend/Formatter')),
    });

    // Add routes for each category
    categories?.forEach(category => {
        routes.push({
            path: `/formattor/${category.id}`,
            element: lazy(() => import('../pages/frontend/Formatter'))
        });

        // Add routes for each subcategory
        category.subCategories.forEach(subcategory => {
            routes.push({
                path: subcategory.path,
                element: lazy(() => import('../pages/frontend/Formatter'))
            });
        });
    });

    return routes;
};

// Export all paths as an array for easy reference
export const getAllPaths = () => {
    const paths: string[] = ['/formatter'];

    categories?.forEach(category => {
        paths.push(`/formatter/${category.id}`);

        category.subCategories.forEach(subcategory => {
            paths.push(subcategory.path);
        });
    });

    return paths;
};